import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, RefreshCw, Loader2, RotateCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function elapsedLabel(startedAt) {
  if (!startedAt) return '—';
  const start = new Date(startedAt);
  const diffH = Math.floor((Date.now() - start.getTime()) / 3600000);
  if (diffH < 24) return `${diffH} h`;
  return `${Math.floor(diffH / 24)} día(s)`;
}

const STATUS_META = {
  pending_bcv: { label: 'En liquidación', cls: 'bg-amber-100 text-amber-700' },
  stopped: { label: 'Sin resolver', cls: 'bg-orange-100 text-orange-700' },
  rejected: { label: 'Rechazado', cls: 'bg-red-100 text-red-700' },
};

export default function DomiciliacionPendingPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/orders/domiciliacion/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (orderId) => {
    setRetryingId(orderId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/orders/${orderId}/domiciliacion/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setToast({ ok: res.data.success || res.data.pending, msg: `[${res.data.codigo}] ${res.data.mensaje}` });
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setToast({ ok: false, msg: typeof d === 'string' ? d : (err.message) });
    } finally {
      setRetryingId(null);
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden mb-8" data-testid="domiciliacion-pending-panel">
      <div className="px-6 py-4 border-b border-amber-200 bg-amber-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg font-semibold" style={{ color: '#051C2C' }}>
            Domiciliación en liquidación
          </h2>
          {rows.length > 0 && (
            <span className="ml-1 text-xs font-semibold bg-amber-600 text-white rounded-full px-2 py-0.5" data-testid="dom-pending-count">{rows.length}</span>
          )}
        </div>
        <button onClick={load} className="p-1.5 rounded hover:bg-amber-100 text-amber-700" data-testid="dom-pending-refresh" title="Actualizar">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <p className="px-6 pt-3 text-xs text-gray-500">
        Cobros por domiciliación que el BCV aún está liquidando. El sistema los confirma automáticamente; los marcados como demorados llevan varios días sin resolverse.
      </p>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : (
        <div className="overflow-x-auto p-4">
          <table className="w-full text-sm" data-testid="dom-pending-table">
            <thead className="text-gray-500">
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Orden</th>
                <th className="px-3 py-2 font-medium">Monto</th>
                <th className="px-3 py-2 font-medium">Pagador</th>
                <th className="px-3 py-2 font-medium">Tiempo</th>
                <th className="px-3 py-2 font-medium">Consultas</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending_bcv;
                const canRetry = !!r.transaccionId && r.status !== 'rejected';
                return (
                  <tr key={r.order_id} className="border-t hover:bg-gray-50" data-testid={`dom-pending-row-${r.order_id}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-700">{r.order_code}</td>
                    <td className="px-3 py-2 font-mono">{Number(r.amount).toLocaleString('es-VE')} {r.currency}</td>
                    <td className="px-3 py-2 text-gray-600">{r.payer_name || '—'}</td>
                    <td className="px-3 py-2">{elapsedLabel(r.started_at || r.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-gray-500">{r.poll_count}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>
                        {r.alerted && <AlertTriangle className="w-3 h-3" />}
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canRetry ? (
                        <button
                          onClick={() => retry(r.order_id)}
                          disabled={retryingId === r.order_id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                          data-testid={`dom-retry-${r.order_id}`}
                          title="Reconsultar el estado de liquidación en el banco"
                        >
                          {retryingId === r.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                          Reintentar
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {toast && (
        <div className={`mx-4 mb-4 px-4 py-2 rounded-lg text-sm ${toast.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="dom-retry-toast">
          {toast.msg}
        </div>
      )}
    </div>
  );
}
